/**
 * Safari Built-in Custom Elements Polyfill
 *
 * Safari supports autonomous custom elements (<my-tag>) natively,
 * but NOT customized built-ins (<div is="my-tag"> / { extends: 'div' }).
 *
 * This polyfill patches ONLY that gap. It does NOT touch:
 *   - HTMLElement or any other global class
 *   - Element.prototype.setAttribute / removeAttribute  <-- KEY difference from broken polyfill
 *   - Autonomous custom elements (menu-drawer, side-drawer, etc.)
 *
 * Elements this fixes for your theme:
 *   variant-media-grouped   { extends: 'div' }
 *   collapsible-details     { extends: 'details' }
 *   dropdown-details        { extends: 'details' }
 *   mega-menu-details       { extends: 'details' }
 */
(function () {
  'use strict';

  // 1. Only run if Safari is actually missing built-in extends support
  function needsPolyfill() {
    try {
      if (!window.customElements) return true;
      var id = '_cetest' + Math.random().toString(36).slice(2);
      function T() { return Reflect.construct(HTMLDivElement, [], T); }
      Object.setPrototypeOf(T.prototype, HTMLDivElement.prototype);
      Object.setPrototypeOf(T, HTMLDivElement);
      customElements.define(id, T, { extends: 'div' });
      return !(document.createElement('div', { is: id }) instanceof T);
    } catch (e) { return true; }
  }

  if (!needsPolyfill()) return;

  // 2. Storage
  // builtins['div']['variant-media-grouped'] = VariantMediaGrouped
  var builtins = Object.create(null);
  var upgraded = new WeakSet();

  // 3. Upgrade one element: set its prototype chain and call lifecycle hooks
  function upgradeOne(el) {
    if (upgraded.has(el)) return;
    var tag = el.tagName.toLowerCase();
    var isVal = el.getAttribute('is');
    if (!isVal) return;
    var map = builtins[tag];
    if (!map || !map[isVal]) return;

    var Ctor = map[isVal];
    upgraded.add(el);
    Object.setPrototypeOf(el, Ctor.prototype);

    // Replay attributeChangedCallback for attributes already on the element
    var observed = Ctor.observedAttributes;
    if (Array.isArray(observed)) {
      for (var i = 0; i < observed.length; i++) {
        var attr = observed[i];
        if (el.hasAttribute(attr) && typeof el.attributeChangedCallback === 'function') {
          try { el.attributeChangedCallback(attr, null, el.getAttribute(attr)); } catch(e) {}
        }
      }
    }

    // Call connectedCallback if the element is already in the DOM
    if (el.isConnected && typeof el.connectedCallback === 'function') {
      try { el.connectedCallback(); } catch(e) {}
    }
  }

  // 4. Walk a DOM subtree upgrading every matching element
  function upgradeAll(root) {
    if (!root || root.nodeType !== 1) return;
    Object.keys(builtins).forEach(function(tag) {
      Object.keys(builtins[tag]).forEach(function(isVal) {
        var sel = tag + '[is="' + isVal + '"]';
        if (root.matches && root.matches(sel)) upgradeOne(root);
        (root.querySelectorAll(sel) || []).forEach && 
          Array.prototype.forEach.call(root.querySelectorAll(sel), upgradeOne);
      });
    });
  }

  // 5. Intercept customElements.define — autonomous elements pass through untouched
  var _define = customElements.define.bind(customElements);
  var _get    = customElements.get.bind(customElements);

  customElements.define = function(name, Ctor, opts) {
    if (!opts || !opts.extends) {
      return _define(name, Ctor, opts);   // autonomous — totally unchanged
    }
    // Built-in extend: register in our map then upgrade existing DOM
    var tag = opts.extends.toLowerCase();
    if (!builtins[tag]) builtins[tag] = Object.create(null);
    builtins[tag][name] = Ctor;
    if (document.documentElement) upgradeAll(document.documentElement);
  };

  // 6. Make customElements.get aware of our registry (theme uses this as a guard)
  customElements.get = function(name) {
    var n = _get(name);
    if (n) return n;
    return Object.keys(builtins).reduce(function(found, tag) {
      return found || builtins[tag][name] || undefined;
    }, undefined);
  };

  // 7. Override document.createElement so `createElement('div', {is:'foo'})` works
  var _create = document.createElement.bind(document);
  document.createElement = function(tag, opts) {
    var el = _create(tag);
    if (opts && opts.is) {
      el.setAttribute('is', opts.is);
      Promise.resolve().then(function() { upgradeOne(el); });
    }
    return el;
  };

  // 8. Watch the DOM for dynamically added elements (e.g. after fetch/render)
  new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
      m.addedNodes.forEach(function(n) {
        if (n.nodeType === 1) upgradeAll(n);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

})();